// Story 11.4 — Panel inferior del editor con feed de logs en vivo (WS),
// filtros, contadores, y controles Pause/Clear.

import { Pause, Play, Trash2, X } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/ui/button';
import { useWsEvent } from '@/hooks/use-ws-event';
import { envelopeToLog, type LogEntry } from '@/lib/event-to-log';
import {
  appendToBuffer,
  countSeverities,
  filterLogs,
  initialFilters,
  isScrolledToBottom,
  type LogFilters,
} from '@/lib/log-buffer';
import { cn } from '@/lib/utils';
import { LogEntryRow } from './LogEntry';
import { LogFiltersBar } from './LogFilters';

interface Props {
  /** Si pasamos un botId, filtramos eventos para mostrar solo los de ese bot. */
  botId?: number;
  /** WS connection state, opcional, se muestra en el footer. */
  wsStatus?: 'connected' | 'reconnecting' | 'disconnected';
  /** Callback cuando el user clickea "Cerrar". */
  onClose?: () => void;
}

/** Story 11.4 — handle imperativo para que `EditorPage` inyecte logs locales
 *  (Deploy/Rollback feedback) sin pasar por WS. */
export interface LogPanelHandle {
  pushLocal(entry: LogEntry): void;
}

const FOOTER_STATUS_LABEL: Record<NonNullable<Props['wsStatus']>, string> = {
  connected: 'Conectado',
  reconnecting: 'Reconectando…',
  disconnected: 'Desconectado',
};

const FOOTER_STATUS_DOT: Record<NonNullable<Props['wsStatus']>, string> = {
  connected: 'bg-success',
  reconnecting: 'bg-warning',
  disconnected: 'bg-destructive',
};

// Allow-list de eventos sin `botId` que SÍ son útiles para el LogPanel
// (telemetría global del runtime, no ligada a bots particulares).
// P-436 (review 11.4): cuando el panel está scoped a un bot, eventos
// SIN `botId` (system/queue) sólo se muestran si están en esta lista.
const GLOBAL_EVENTS_ALLOW = new Set([
  'system:hello',
  'system:ready',
  'queue:saturated',
  'queue:drained',
]);

export const LogPanel = forwardRef<LogPanelHandle, Props>(function LogPanel(
  { botId, wsStatus = 'connected', onClose },
  ref,
): React.ReactElement {
  const [buffer, setBuffer] = useState<LogEntry[]>([]);
  const [paused, setPaused] = useState(false);
  const [filters, setFilters] = useState<LogFilters>(initialFilters);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const botIdRef = useRef(botId);
  botIdRef.current = botId;

  const handleEnvelope = useCallback((envelope: unknown) => {
    if (pausedRef.current) return;
    const log = envelopeToLog(envelope as Parameters<typeof envelopeToLog>[0]);
    if (!log) return;
    const currentBot = botIdRef.current;
    if (currentBot != null) {
      // P-436 (review 11.4): allow-list para eventos SIN `botId`.
      if (log.botId == null) {
        if (!GLOBAL_EVENTS_ALLOW.has(log.eventType)) return;
      } else if (log.botId !== String(currentBot)) {
        return;
      }
    }
    setBuffer((prev) => appendToBuffer(prev, log));
  }, []);

  // Suscribimos a TODOS los eventos del WS (`*`).
  useWsEvent('*', handleEnvelope);

  useImperativeHandle(
    ref,
    () => ({
      pushLocal: (entry) => setBuffer((prev) => appendToBuffer(prev, entry)),
    }),
    [],
  );

  const filtered = useMemo(() => filterLogs(buffer, filters), [buffer, filters]);
  const counts = useMemo(() => countSeverities(buffer), [buffer]);

  const listRef = useRef<HTMLUListElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // P-434 (review 11.4): auto-scroll necesita re-correr cada vez que llega
  // un mensaje nuevo (no solo cuando cambia `isAtBottom`). Antes el effect
  // dep array era `[isAtBottom]` → si el user ya estaba al fondo y llegaba
  // un log, el effect no re-corría → scroll quedaba pegado encima de la
  // línea recién añadida. Ahora dep includes `filtered.length`.
  // Tolerancia bajada de 50px → 8px (`isScrolledToBottom` ya tolera 50px;
  // mantenemos esa tolerancia para `wheel` upward intent — no ajustamos
  // acá para no romper el helper).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-trigger by length
  useEffect(() => {
    if (!isAtBottom) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [isAtBottom, filtered.length]);

  function handleScroll(): void {
    const el = listRef.current;
    if (!el) return;
    setIsAtBottom(isScrolledToBottom(el.scrollHeight, el.scrollTop, el.clientHeight));
  }

  return (
    <section
      className="flex h-full flex-col border-t border-border bg-card"
      aria-label="Panel de logs"
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
        <span className="font-semibold">Logs</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{buffer.length}</span>
        {counts.errors > 0 && (
          <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] text-destructive">
            {counts.errors} error{counts.errors === 1 ? '' : 'es'}
          </span>
        )}
        {counts.warnings > 0 && (
          <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] text-warning">
            {counts.warnings} warning{counts.warnings === 1 ? '' : 's'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant={paused ? 'default' : 'ghost'}
            onClick={() => setPaused((v) => !v)}
            aria-pressed={paused}
            title={paused ? 'Reanudar feed' : 'Pausar feed'}
          >
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            {paused ? 'Reanudar' : 'Pausar'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setBuffer([])}
            aria-label="Limpiar logs"
            title="Limpiar (no toca el event_log del backend)"
          >
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </Button>
          {onClose && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              aria-label="Cerrar panel"
              className="h-7 w-7"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </header>
      <LogFiltersBar filters={filters} onChange={setFilters} />
      <ul
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
        aria-label="Lista de logs"
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-muted-foreground">
            {buffer.length === 0
              ? 'Sin logs todavía. Los eventos del bot aparecerán acá en vivo.'
              : 'Ningún log matchea los filtros activos.'}
          </li>
        ) : (
          filtered.map((entry) => (
            <LogEntryRow key={entry.id} entry={entry} searchTerm={filters.search} />
          ))
        )}
      </ul>
      <footer className="flex items-center justify-between gap-2 border-t border-border px-3 py-1 text-[10px] text-muted-foreground">
        <span>
          Mostrando {filtered.length} de {buffer.length} {paused && '· feed pausado'}
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 rounded-full', FOOTER_STATUS_DOT[wsStatus])}
          />
          {FOOTER_STATUS_LABEL[wsStatus]}
        </span>
      </footer>
    </section>
  );
});
